/* ============================================================
   ARTTEESHOW. V3 · ERASED product page
   Behaviour for sections/ats-erased.liquid. No libraries.

   Add to bag posts the section's native Shopify product form to /cart/add.js and then
   fires window 'cart:refresh', exactly as sections/product-brutalist.liquid does on every
   other product page. The header's cart drawer listens for that event, refreshes from
   /cart.js and opens. Nothing here touches the header, the drawer or the cart page.
   ============================================================ */

(() => {
  'use strict';

  if (window.__atsErased) return;
  window.__atsErased = true;

  const instances = new Map();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehaviour = () => (reducedMotion() ? 'instant' : 'smooth');

  /* Brand copy never carries an em or en dash, including messages passed through from Shopify. */
  const tidy = (text) => String(text || '').replace(/\s*[\u2013\u2014]\s*/g, ' · ').trim();

  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- The story ---------------------------------------------------------
     Shopify's editor leaves empty paragraphs (&nbsp;, a lone <br>, editor attributes)
     and runs of <br> that open large gaps. Collapse each run to one <br>, drop <br> at
     the start or end of a block, then remove blocks with nothing left in them. */
  const BLANK = /[\s ​‌‍﻿]/g;
  const isBr = (node) => node && node.nodeName === 'BR';
  const meaningful = (node, dir) => {
    let n = node && node[dir];
    while (n && n.nodeType === 3 && !n.textContent.replace(BLANK, '')) n = n[dir];
    return n;
  };
  const normaliseRte = (rte) => {
    rte.querySelectorAll('br').forEach((br) => {
      if (isBr(meaningful(br, 'nextSibling'))) br.remove();
    });
    rte.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((block) => {
      let first = block.firstChild;
      while (first && (isBr(first) || (first.nodeType === 3 && !first.textContent.replace(BLANK, '')))) {
        const next = first.nextSibling;
        if (isBr(first)) first.remove();
        first = next;
      }
      let last = block.lastChild;
      while (last && (isBr(last) || (last.nodeType === 3 && !last.textContent.replace(BLANK, '')))) {
        const prev = last.previousSibling;
        if (isBr(last)) last.remove();
        last = prev;
      }
    });
    [...rte.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6')].reverse().forEach((block) => {
      if (block.querySelector('img, picture, video, iframe, svg, hr')) return;
      if (!block.textContent.replace(BLANK, '')) block.remove();
    });
    while (isBr(rte.firstElementChild) && !meaningful(rte.firstElementChild, 'previousSibling')) rte.firstElementChild.remove();
  };

  /* ---------- Drop time -------------------------------------------------------
     ISO 8601 with an offset is read as written. Without one it is read as UK time. */
  const parseDrop = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return NaN;
    if (/(z|[+-]\d{2}:?\d{2})$/i.test(value)) return Date.parse(value);
    const wall = value.slice(11, 16);
    for (const offset of ['+01:00', '+00:00']) {
      const t = Date.parse(value + offset);
      if (Number.isNaN(t)) return NaN;
      try {
        const uk = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).format(t);
        if (!wall || uk === wall) return t;
      } catch (e) {
        return t;
      }
    }
    return Date.parse(value + '+00:00');
  };

  const ukDropLabel = (t) => {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(t);
      const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
      return `Drops ${get('weekday')} ${get('day')} ${get('month')} · ${get('hour')}:${get('minute')} UK time`;
    } catch (e) {
      return '';
    }
  };

  class ErasedPage {
    constructor(root) {
      this.root = root;
      this.cleanups = [];
      this.state = root.dataset.atsState;

      root.classList.add('ats-js');
      root.querySelectorAll('[data-ats-rte]').forEach(normaliseRte);

      this.initHeaderOffset();
      this.initReveal();
      this.initGallery();
      this.initAccordions();
      this.initRailTop();

      if (this.state === 'live') this.initBuy();
      if (this.state === 'upcoming') this.initUpcoming();
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    destroy() {
      this.cleanups.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          /* already gone */
        }
      });
      this.cleanups = [];
    }

    /* ---------- Header offset ---------------------------------------------------
       The live header is fixed and publishes no height. Measure it while it is in its
       unscrolled (tallest) state; otherwise keep the CSS value, which matches it. */
    initHeaderOffset() {
      const header = document.getElementById('HeaderWrapper');
      if (!header) return;
      const measure = () => {
        if (header.classList.contains('header-scrolled')) return;
        const h = Math.round(header.getBoundingClientRect().height);
        if (h > 20 && h < 200) this.root.style.setProperty('--header-h', `${h}px`);
      };
      measure();
      let frame = 0;
      this.on(window, 'resize', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      });
    }

    /* ---------- Reveal ------------------------------------------------------------ */
    initReveal() {
      const nodes = [...this.root.querySelectorAll('[data-ats-reveal]')];
      if (!nodes.length) return;
      const showAll = () => nodes.forEach((n) => n.classList.add('is-in'));

      if (reducedMotion() || !('IntersectionObserver' in window)) {
        showAll();
        return;
      }

      const groups = new Map();
      nodes.forEach((n) => {
        const group = n.closest('[data-ats-reveal-group]');
        if (!group) return;
        const list = groups.get(group) || [];
        n.style.setProperty('--reveal-delay', `${Math.min(list.length, 8) * 0.06}s`);
        list.push(n);
        groups.set(group, list);
      });

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
      const start = () => {
        const vh = window.innerHeight || 800;
        nodes.forEach((n) => {
          const box = n.getBoundingClientRect();
          if (box.top < vh && box.bottom > 0) n.classList.add('is-in');
          else io.observe(n);
        });
      };
      /* A background tab never paints, so the observer would never fire. Wait for it. */
      if (document.visibilityState === 'hidden') {
        const onVisible = () => {
          if (document.visibilityState !== 'visible') return;
          document.removeEventListener('visibilitychange', onVisible);
          start();
        };
        this.on(document, 'visibilitychange', onVisible);
      } else {
        start();
      }
      this.cleanups.push(() => io.disconnect());
    }

    /* ---------- Gallery: swipe counter at 1040px and below -------------------------- */
    initGallery() {
      const track = this.root.querySelector('[data-ats-track]');
      const counter = this.root.querySelector('[data-ats-count]');
      if (!track) return;
      const slides = [...track.querySelectorAll('[data-ats-slide]')];
      const narrow = window.matchMedia('(max-width: 1040px)');

      const setFocusable = () => {
        if (narrow.matches && slides.length > 1) track.setAttribute('tabindex', '0');
        else track.removeAttribute('tabindex');
      };
      setFocusable();
      if (narrow.addEventListener) {
        narrow.addEventListener('change', setFocusable);
        this.cleanups.push(() => narrow.removeEventListener('change', setFocusable));
      }

      if (!counter || slides.length < 2) return;
      let current = 0;
      let frame = 0;
      const update = () => {
        const origin = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).scrollPaddingLeft || 0);
        let best = 0;
        let bestDistance = Infinity;
        slides.forEach((slide, i) => {
          const d = Math.abs(slide.getBoundingClientRect().left - origin);
          if (d < bestDistance) {
            bestDistance = d;
            best = i;
          }
        });
        if (best !== current) {
          current = best;
          counter.textContent = String(best + 1);
        }
      };
      this.on(track, 'scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      }, { passive: true });
    }

    /* ---------- Accordions and the size guide link ---------------------------------- */
    initAccordions() {
      const items = [...this.root.querySelectorAll('[data-ats-acc]')];
      items.forEach((details) => {
        const summary = details.querySelector('summary');
        const sync = () => summary && summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        sync();
        this.on(details, 'toggle', sync);
      });

      const guide = this.root.querySelector('[data-ats-size-guide]');
      if (!guide) return;
      const target = document.getElementById(guide.getAttribute('aria-controls'));
      if (!target) {
        guide.hidden = true;
        return;
      }
      const syncGuide = () => guide.setAttribute('aria-expanded', target.open ? 'true' : 'false');
      syncGuide();
      this.on(target, 'toggle', syncGuide);
      this.on(guide, 'click', () => {
        target.open = true;
        syncGuide();
        const summary = target.querySelector('summary');
        if (summary) summary.focus({ preventScroll: true });

        const rail = target.closest('[data-ats-rail]');
        if (!rail || getComputedStyle(rail).position !== 'sticky') {
          target.scrollIntoView({ behavior: scrollBehaviour(), block: 'start' });
          return;
        }
        /* Inside the sticky rail, scrollIntoView overshoots: the rail travels with the
           page only until its bottom edge is in view. Scroll exactly that far, or less
           if the accordion reaches the top first. */
        requestAnimationFrame(() => {
          const header = parseFloat(getComputedStyle(this.root).getPropertyValue('--header-h')) || 70;
          const want = target.getBoundingClientRect().top - (header + 16);
          const travel = rail.getBoundingClientRect().bottom - (window.innerHeight - 16);
          const dy = Math.max(0, Math.min(want, travel));
          if (dy > 0) window.scrollBy({ top: dy, behavior: scrollBehaviour() });
        });
      });
    }

    /* ---------- Sticky rail --------------------------------------------------------
       Sticks below the header while it fits the screen. When it is taller than the
       screen it travels with the scroll: going down it moves with the page until its
       bottom edge is in view, then holds; going up it moves back until its top sits
       under the header again. Nothing in it is out of reach, and Add to bag returns
       as soon as the reader scrolls up. */
    initRailTop() {
      const rail = this.root.querySelector('[data-ats-rail]');
      if (!rail) return;
      const wide = window.matchMedia('(min-width: 1041px)');
      let offset = 86;
      let min = 86;
      let top = 86;
      let lastY = window.scrollY;

      const apply = () => rail.style.setProperty('--ats-rail-top', `${Math.round(top)}px`);
      const measure = () => {
        if (!wide.matches) {
          rail.style.removeProperty('--ats-rail-top');
          return;
        }
        const header = parseFloat(getComputedStyle(this.root).getPropertyValue('--header-h')) || 70;
        offset = header + 16;
        min = Math.min(offset, window.innerHeight - 16 - rail.offsetHeight);
        top = Math.max(min, Math.min(offset, top));
        apply();
      };
      const onScroll = () => {
        const y = window.scrollY;
        const dy = y - lastY;
        lastY = y;
        if (!wide.matches || min >= offset) return;
        const next = Math.max(min, Math.min(offset, top - dy));
        if (next !== top) {
          top = next;
          apply();
        }
      };

      top = offset;
      measure();
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(measure);
        ro.observe(rail);
        this.cleanups.push(() => ro.disconnect());
      }
      this.on(window, 'resize', measure);
      this.on(window, 'scroll', onScroll, { passive: true });
    }

    /* ---------- Live: options, form, dock -------------------------------------------- */
    initBuy() {
      const dataEl = this.root.querySelector('[data-ats-product]');
      const form = this.root.querySelector('form.ats-buy');
      if (!dataEl || !form) return;

      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      const variants = data.variants || [];
      const sizeIndex = typeof data.sizeIndex === 'number' ? data.sizeIndex : -1;
      const idInput = form.querySelector('[data-ats-variant-input]');
      const addBtn = form.querySelector('[data-ats-add]');
      const addLabel = form.querySelector('[data-ats-add-label]');
      const buyNow = form.querySelector('[data-ats-buy-now]');
      const errorEl = form.querySelector('[data-ats-error]');
      const priceNow = this.root.querySelector('[data-ats-price-now]');
      const priceWas = this.root.querySelector('[data-ats-price-was]');
      const sizeMsg = this.root.querySelector('[data-ats-size-msg]');
      const status = this.root.querySelector('[data-ats-status]');
      const groups = [...this.root.querySelectorAll('[data-ats-option]')];
      const sizeGroup = groups.find((g) => Number(g.dataset.atsOption) === sizeIndex) || null;

      const initial = variants.find((v) => String(v.id) === String(idInput.value)) || variants[0];
      if (!initial) return;

      const selected = initial.options.slice();
      let sizeChosen = sizeIndex < 0 || Boolean(data.sizeChosen);
      let busy = false;
      let current = initial;
      let labelTimer = 0;

      const announce = (text) => {
        if (!status) return;
        status.textContent = '';
        window.setTimeout(() => {
          status.textContent = text;
        }, 60);
      };

      const matchesOthers = (variant, skipIndex) =>
        variant.options.every((value, i) => i === skipIndex || i === sizeIndex || value === selected[i]);

      const resolve = () => {
        if (sizeChosen) {
          return variants.find((v) => v.options.every((value, i) => value === selected[i])) || null;
        }
        return (
          variants.find((v) => v.available && matchesOthers(v, -1)) ||
          variants.find((v) => matchesOthers(v, -1)) ||
          null
        );
      };

      /* Dock: moved to <body> so position: fixed means the screen, not #MainContent. */
      const dock = this.root.querySelector('[data-ats-dock]');
      const dockPrice = dock && dock.querySelector('[data-ats-dock-price]');
      const dockBtn = dock && dock.querySelector('[data-ats-dock-add]');
      if (dock) {
        document.body.appendChild(dock);
        this.cleanups.push(() => dock.remove());
      }

      const setButtons = (label, disabled) => {
        if (addLabel) addLabel.textContent = label;
        addBtn.disabled = disabled;
        if (dockBtn) {
          dockBtn.textContent = label;
          dockBtn.disabled = disabled;
        }
      };

      const restingLabel = () => (sizeChosen && current && !current.available ? 'Sold out' : 'Add to bag');

      const render = ({ fromUser = false } = {}) => {
        groups.forEach((group) => {
          const index = Number(group.dataset.atsOption);
          const isSize = index === sizeIndex;
          group.querySelectorAll('[data-ats-value]').forEach((btn) => {
            const value = btn.dataset.atsValue;
            const available = isSize
              ? variants.some((v) => v.available && v.options[sizeIndex] === value && matchesOthers(v, -1))
              : variants.some((v) => v.available && v.options[index] === value);
            btn.disabled = !available;
            const note = btn.querySelector('[data-ats-soldout-note]');
            if (note) note.hidden = available;
            const on = value === selected[index] && (!isSize || sizeChosen);
            btn.classList.toggle('is-on', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          const valueLabel = group.querySelector('[data-ats-opt-value]');
          if (valueLabel) valueLabel.textContent = selected[index];
        });

        current = resolve();
        if (sizeChosen && (!current || !current.available) && sizeIndex >= 0) {
          /* The chosen size does not exist in this colour. Ask again rather than guess. */
          const sizeBtn = sizeGroup && sizeGroup.querySelector(`[data-ats-value="${CSS.escape(selected[sizeIndex])}"]`);
          if (!sizeBtn || sizeBtn.disabled) {
            sizeChosen = false;
            current = resolve();
            if (sizeBtn) {
              sizeBtn.classList.remove('is-on');
              sizeBtn.setAttribute('aria-pressed', 'false');
            }
          }
        }
        if (!current) return;

        if (String(idInput.value) !== String(current.id)) {
          idInput.value = current.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (priceNow) priceNow.textContent = current.price;
        if (priceWas) {
          priceWas.textContent = current.compare || '';
          priceWas.hidden = !current.compare;
        }
        if (dockPrice) dockPrice.textContent = current.price;

        if (!busy) setButtons(restingLabel(), Boolean(sizeChosen && !current.available));
        if (buyNow) buyNow.classList.toggle('is-gated', !sizeChosen);
        if (sizeMsg && sizeChosen) sizeMsg.hidden = true;
        if (errorEl && fromUser) errorEl.hidden = true;

        if (fromUser) {
          /* A full choice goes in the URL, as the theme's picker does. A half choice
             (colour changed, size not chosen yet) takes a stale ?variant= out. */
          try {
            const url = new URL(window.location.href);
            if (sizeChosen) url.searchParams.set('variant', current.id);
            else url.searchParams.delete('variant');
            window.history.replaceState(window.history.state, '', url.toString());
          } catch (e) {
            /* history unavailable: nothing to update */
          }
        }
      };

      /* One available size only: choose it, so the flow never stalls on a single option. */
      if (!sizeChosen && sizeGroup) {
        const open = [...sizeGroup.querySelectorAll('[data-ats-value]')].filter((b) => !b.disabled);
        if (open.length === 1) {
          selected[sizeIndex] = open[0].dataset.atsValue;
          sizeChosen = true;
        }
      }

      groups.forEach((group) => {
        this.on(group, 'click', (event) => {
          const btn = event.target.closest('[data-ats-value]');
          if (!btn || btn.disabled || !group.contains(btn)) return;
          const index = Number(group.dataset.atsOption);
          selected[index] = btn.dataset.atsValue;
          if (index === sizeIndex) sizeChosen = true;
          render({ fromUser: true });
        });
      });

      const promptSize = () => {
        if (sizeMsg) sizeMsg.hidden = false;
        if (sizeGroup) {
          sizeGroup.scrollIntoView({ behavior: scrollBehaviour(), block: 'center' });
          const first = sizeGroup.querySelector('[data-ats-value]:not([disabled])');
          if (first) first.focus({ preventScroll: true });
        }
      };

      /* Buy it now: Shopify reads the form's variant id when clicked. Hold it until a size
         is chosen; capture phase stops the click before Shopify's own handler sees it. */
      if (buyNow) {
        this.on(
          buyNow,
          'click',
          (event) => {
            if (sizeChosen) return;
            event.preventDefault();
            event.stopPropagation();
            promptSize();
          },
          true
        );
      }

      const showError = (message) => {
        if (!errorEl) return;
        errorEl.textContent = tidy(message) || 'Could not add to bag. Try again.';
        errorEl.hidden = false;
      };

      this.on(form, 'submit', async (event) => {
        event.preventDefault();
        if (busy) return;
        if (!sizeChosen) {
          promptSize();
          return;
        }
        if (!current || !current.available) return;

        busy = true;
        window.clearTimeout(labelTimer);
        if (errorEl) errorEl.hidden = true;
        setButtons('Adding', true);
        addBtn.setAttribute('aria-busy', 'true');

        let added = false;
        try {
          const response = await fetch(data.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form),
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok || body.status) {
            showError(body.description || body.message);
          } else {
            added = true;
          }
        } catch (e) {
          showError('');
        }

        busy = false;
        addBtn.removeAttribute('aria-busy');

        if (added) {
          setButtons('Added to bag', true);
          announce('Added to bag');
          /* The same event every other product page fires: the header drawer refreshes and opens. */
          window.dispatchEvent(new Event('cart:refresh'));
          labelTimer = window.setTimeout(() => setButtons(restingLabel(), false), 2000);
        } else {
          setButtons(restingLabel(), Boolean(sizeChosen && current && !current.available));
        }
      });
      this.cleanups.push(() => window.clearTimeout(labelTimer));

      render();

      /* Dock shows once the rail's own button has scrolled up out of view, and steps
         aside when the end of the section comes up so it never covers the footer.
         Measured on scroll rather than with IntersectionObserver: a fast fling can
         carry the button from below the screen to above it between two frames, and
         an observer never reports a crossing it did not see. */
      if (dock) {
        let up = false;
        let frame = 0;
        const check = () => {
          frame = 0;
          const buttonPast = addBtn.getBoundingClientRect().bottom < 0;
          const endReached = this.root.getBoundingClientRect().bottom <= window.innerHeight;
          const next = buttonPast && !endReached;
          if (next === up) return;
          up = next;
          dock.classList.toggle('is-up', up);
          dock.setAttribute('aria-hidden', up ? 'false' : 'true');
          dock.inert = !up;
        };
        const schedule = () => {
          if (!frame) frame = requestAnimationFrame(check);
        };
        this.on(window, 'scroll', schedule, { passive: true });
        this.on(window, 'resize', schedule);
        this.cleanups.push(() => cancelAnimationFrame(frame));
        check();
      }
    }

    /* ---------- Upcoming: countdown and the notify form ------------------------------ */
    initUpcoming() {
      /* After a successful sign-up Shopify redirects back to #the-form. Focus the
         message once the browser has finished jumping to that anchor, so screen
         readers hear it. */
      const ok = this.root.querySelector('[data-ats-notify-ok]');
      if (ok) {
        const focusOk = () => window.setTimeout(() => ok.focus({ preventScroll: true }), 60);
        if (document.readyState === 'complete') focusOk();
        else this.on(window, 'load', focusOk, { once: true });
      }

      const box = this.root.querySelector('[data-ats-countdown]');
      if (!box) return;
      const target = parseDrop(box.dataset.atsTarget);
      if (Number.isNaN(target)) {
        box.hidden = true;
        return;
      }

      const when = box.querySelector('[data-ats-drop-when]');
      const label = ukDropLabel(target);
      if (when && label) {
        when.textContent = label;
        when.hidden = false;
      }

      const cells = {
        d: box.querySelector('[data-ats-cd="d"]'),
        h: box.querySelector('[data-ats-cd="h"]'),
        m: box.querySelector('[data-ats-cd="m"]'),
      };
      const now = this.root.querySelector('[data-ats-now]');
      const notify = this.root.querySelector('form.ats-notify');
      let timer = 0;
      let last = '';

      const dropNow = () => {
        window.clearInterval(timer);
        box.hidden = true;
        if (notify) notify.hidden = true;
        if (now) now.hidden = false;
      };

      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          dropNow();
          return;
        }
        const total = Math.ceil(diff / 60000);
        const d = Math.floor(total / 1440);
        const h = Math.floor((total % 1440) / 60);
        const m = total % 60;
        const key = `${d}:${h}:${m}`;
        if (key === last) return;
        last = key;
        if (cells.d) cells.d.textContent = pad(d);
        if (cells.h) cells.h.textContent = pad(h);
        if (cells.m) cells.m.textContent = pad(m);
        box.hidden = false;
        if (now) now.hidden = true;
      };

      tick();
      timer = window.setInterval(tick, 1000);
      this.on(document, 'visibilitychange', tick);
      this.cleanups.push(() => window.clearInterval(timer));
    }
  }

  const init = (root) => {
    const id = root && root.dataset.sectionId;
    if (!id || instances.has(id)) return;
    instances.set(id, new ErasedPage(root));
  };

  const destroy = (id) => {
    const instance = instances.get(id);
    if (!instance) return;
    instance.destroy();
    instances.delete(id);
  };

  const start = () => document.querySelectorAll('[data-ats-root]').forEach(init);

  /* Theme editor: sections are re-rendered in place. */
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-ats-root]');
    if (root) {
      destroy(root.dataset.sectionId);
      init(root);
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-ats-root]');
    if (root) destroy(root.dataset.sectionId);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
